import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { authenticationGuard, fetchCommonData } from './guards';

const AddTentativePickupStops = () =>
  import('@/pages/Admin/Logistics/AddTentativePickupStops/AddTentativePickupStops.vue');
const EtosManager = () => import('@/pages/Admin/Logistics/EtosManager/EtosManager.vue');
const BuyerVerification = () => import('@/pages/Admin/Logistics/BuyerVerification/index.vue');
const Buyers = () => import('@/pages/Admin/Logistics/BuyerVerification/Buyers.vue');
const UnlinkedCustomers = () => import('@/pages/Admin/Logistics/BuyerVerification/UnlinkedCustomers.vue');
const ConfirmInventoryTask = () => import('@/pages/Admin/Logistics/ConfirmInventoryTask/index.vue');
const PickupStops = () => import('@/pages/Admin/Logistics/ConfirmInventoryTask/PickupStops.vue');
const DeliveryStops = () => import('@/pages/Admin/Logistics/ConfirmInventoryTask/DeliveryStops.vue');
const CreateBuyer = () => import('@/pages/Admin/Logistics/CreateBuyer/index.vue');
const DefaultBuyerStoreHours = () => import('@/pages/Admin/Logistics/DefaultBuyerStoreHours/index.vue');
const Transfers = () => import('@/pages/Admin/Logistics/InboundOrderMapping/Transfers.vue');
const Items = () => import('@/pages/Admin/Logistics/InboundOrderMapping/Items.vue');
const ItemsCreateEditMapping = () => import('@/pages/Admin/Logistics/InboundOrderMapping/ItemsCreateEditMapping.vue');

async function fetchTranslations() {
  await loadTranslations('admin');
}

export function buyerVerificationTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    { to: { name: ROUTE_NAMES.UNVERIFIED_BUYERS }, label: i18n.t('admin.logistics.buyerVerification.tabs.buyers') },
    {
      to: { name: ROUTE_NAMES.UNLINKED_CUSTOMERS },
      label: i18n.t('admin.logistics.buyerVerification.tabs.unlinkedCustomers'),
    },
  ]);
}

export function confirmInventoryTaskTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    { to: { name: ROUTE_NAMES.PICK_UP }, label: i18n.t('admin.logistics.confirmInventoryTask.tabs.pickup') },
    { to: { name: ROUTE_NAMES.DELIVERY }, label: i18n.t('admin.logistics.confirmInventoryTask.tabs.delivery') },
  ]);
}

export function inboundOrderMappingTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    { to: { name: ROUTE_NAMES.TRANSFERS }, label: i18n.t('admin.logistics.inboundOrderMapping.tabs.transfers') },
    { to: { name: ROUTE_NAMES.ITEMS }, label: i18n.t('admin.logistics.inboundOrderMapping.tabs.items') },
  ]);
}

const logisticsAdminRoutes: RouteRecordRaw[] = [
  {
    path: '/ll/logistics-admin/',
    meta: {
      permissions: [{ rules: [({ user }) => user.isLeaflinkStaff && user.isSuperuser] }],
    },
    children: [
      {
        path: 'add-tentative-pickup-stops/',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.ADD_TENTATIVE_PICKUP_STOPS,
        component: AddTentativePickupStops,
        meta: {
          title: () => i18n.t('admin.logistics.addTentativePickupStops.title'),
        },
      },
      {
        path: 'etos-manager',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.ETOS_MANAGER,
        component: EtosManager,
        meta: {
          title: () => i18n.t('admin.logistics.etosManager.title'),
        },
      },
      {
        path: 'buyer-verification/',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.BUYER_VERIFICATION,
        component: BuyerVerification,
        meta: {
          tabs: buyerVerificationTabRoutes,
        },
        children: [
          {
            path: '',
            name: ROUTE_NAMES.UNVERIFIED_BUYERS,
            component: Buyers,
            meta: {
              title: () => i18n.t('admin.logistics.buyerVerification.title'),
            },
          },
          {
            path: 'unlinked-customers/',
            name: ROUTE_NAMES.UNLINKED_CUSTOMERS,
            component: UnlinkedCustomers,
            meta: {
              title: () => i18n.t('admin.logistics.buyerVerification.title'),
            },
          },
        ],
      },
      {
        path: 'inbound-order-mapping/',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.TRANSFERS,
        component: Transfers,
        meta: {
          tabs: inboundOrderMappingTabRoutes,
          title: () => i18n.t('admin.logistics.inboundOrderMapping.title'),
          description: () => i18n.t('admin.logistics.inboundOrderMapping.description'),
        },
      },
      {
        path: 'inbound-order-mapping/items/',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.ITEMS,
        component: Items,
        meta: {
          tabs: inboundOrderMappingTabRoutes,
          title: () => i18n.t('admin.logistics.inboundOrderMapping.title'),
          description: () => i18n.t('admin.logistics.inboundOrderMapping.description'),
        },
      },
      {
        path: 'inbound-order-mapping/items/mapping/create',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.ITEMS_CREATE_MAPPING,
        component: ItemsCreateEditMapping,
        meta: {
          title: () => i18n.t('admin.logistics.inboundOrderMapping.actions.createMapping'),
        },
      },
      {
        path: 'inbound-order-mapping/items/mapping/:mappingId/edit',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.ITEMS_EDIT_MAPPING,
        component: ItemsCreateEditMapping,
        props: true,
        meta: {
          title: () => i18n.t('admin.logistics.inboundOrderMapping.actions.editMapping'),
        },
      },
      {
        path: 'create-buyer/:state',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.CREATE_BUYER,
        component: CreateBuyer,
        meta: {
          title: () => i18n.t('admin.logistics.createBuyer.title'),
        },
      },
      {
        path: 'inventory-task/',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.CONFIRM_INVENTORY_TASK,
        component: ConfirmInventoryTask,
        meta: {
          tabs: confirmInventoryTaskTabRoutes,
        },
        children: [
          {
            path: '',
            name: ROUTE_NAMES.PICK_UP,
            component: PickupStops,
            meta: {
              title: () => i18n.t('admin.logistics.confirmInventoryTask.title'),
            },
          },
          {
            path: 'delivery/',
            name: ROUTE_NAMES.DELIVERY,
            component: DeliveryStops,
            meta: {
              title: () => i18n.t('admin.logistics.confirmInventoryTask.title'),
            },
          },
        ],
      },
      {
        path: 'default-buyer-store-hours',
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        name: ROUTE_NAMES.DEFAULT_BUYER_STORE_HOURS,
        component: DefaultBuyerStoreHours,
        meta: {
          title: () => i18n.t('admin.logistics.defaultBuyerStoreHours.title'),
          description: () => i18n.t('admin.logistics.defaultBuyerStoreHours.description'),
        },
      },
      {
        path: ':catchAll(.*)*',
        name: ROUTE_NAMES.WILDCARD,
        redirect: { name: '404' },
      },
    ],
  },
];

export default logisticsAdminRoutes;
